// `/survey/{co}/{token}` (BIZ-CLIENT-HEALTH): a client contact answers an NPS or CSAT survey by
// its link, with no account (the token is the key, like an assessment respondent). The company's
// mark and name, the client, one question — NPS "How likely are you to recommend {company}?"
// 0–10, CSAT "How satisfied are you with {company}'s work?" 1–5 — an optional comment and Send;
// then the thank-you. An answered link says so; an expired or unknown one says it doesn't work.
import { ApiError, type OperationOutput } from "@companyos/sdk";
import { BrandMark, Button, cn, EmptyState, Label, Spinner, Textarea } from "@companyos/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CircleCheckIcon, LinkIcon } from "lucide-react";
import { type ReactNode, useId, useState } from "react";
import type { Api } from "../../api/client";

type View = OperationOutput<"survey.get">;

const surveyKey = (co: string, token: string) => ["survey", co, token] as const;

function Frame({ children, company }: { children: ReactNode; company?: string }) {
  return (
    <main
      id="main"
      className="min-h-svh bg-background px-4 py-8 text-foreground max-md:py-5"
      data-slot="survey-page"
    >
      <div className="mx-auto flex w-full max-w-[560px] flex-col gap-5">
        <div className="flex items-center gap-2.5" data-slot="wordmark">
          <BrandMark name={company ?? "CompanyOS"} size={32} />
          <span className="truncate font-heading text-base font-strong">
            {company ?? "CompanyOS"}
          </span>
        </div>
        {children}
      </div>
    </main>
  );
}

export function SurveyPage({ api, co, token }: { api: Api; co: string; token: string }) {
  const view = useQuery({
    queryKey: surveyKey(co, token),
    queryFn: () => api.client.survey.get({ params: { co, token } }),
    retry: false,
  });
  if (view.isPending)
    return (
      <Frame>
        <div className="flex justify-center py-16" role="status" aria-label="Loading the survey">
          <Spinner />
        </div>
      </Frame>
    );
  if (view.isError || !view.data) {
    const missing = view.error instanceof ApiError && view.error.status === 404;
    return (
      <Frame>
        <EmptyState
          variant="card"
          icon={LinkIcon}
          title={missing ? "This link doesn't work" : "The survey could not be loaded"}
          description={
            missing
              ? "Ask the person who sent it for a new link."
              : "Check your connection and reload the page."
          }
        />
      </Frame>
    );
  }
  const v = view.data;
  return (
    <Frame company={v.company_name}>
      {v.answered ? (
        <EmptyState
          variant="card"
          icon={CircleCheckIcon}
          title="Thank you — your answer is in"
          description={`${v.company_name} reads every answer.`}
        />
      ) : v.expired ? (
        <EmptyState
          variant="card"
          icon={LinkIcon}
          title="This link has expired"
          description="Ask the person who sent it for a new link."
        />
      ) : (
        <Question api={api} co={co} token={token} view={v} />
      )}
    </Frame>
  );
}

function Question({ api, co, token, view }: { api: Api; co: string; token: string; view: View }) {
  const qc = useQueryClient();
  const nps = view.kind === "nps";
  const scores = nps ? Array.from({ length: 11 }, (_, i) => i) : [1, 2, 3, 4, 5];
  const [score, setScore] = useState<number | null>(null);
  const [comment, setComment] = useState("");
  const commentId = useId();
  const send = useMutation({
    mutationFn: () =>
      api.client.survey.answer({
        params: { co, token },
        body: { score: score ?? 0, ...(comment.trim() ? { comment: comment.trim() } : {}) },
      }),
    onSuccess: (v) => qc.setQueryData(surveyKey(co, token), v),
  });
  const question = nps
    ? `How likely are you to recommend ${view.company_name}?`
    : `How satisfied are you with ${view.company_name}'s work?`;
  return (
    <form
      className="flex flex-col gap-5 rounded-lg border border-border bg-card p-5"
      aria-label="Survey"
      onSubmit={(e) => {
        e.preventDefault();
        if (score !== null && !send.isPending) send.mutate();
      }}
    >
      <div className="flex flex-col gap-1">
        <p className="text-sm text-muted-foreground">
          {view.recipient_name} · {view.client_name}
        </p>
        <h1 className="font-heading text-xl font-semibold">{question}</h1>
      </div>
      <div className="flex flex-col gap-1.5">
        <div
          role="radiogroup"
          aria-label={question}
          className={cn("grid gap-1.5", nps ? "grid-cols-11 max-sm:grid-cols-6" : "grid-cols-5")}
        >
          {scores.map((n) => (
            <Button
              key={n}
              type="button"
              role="radio"
              aria-checked={score === n}
              variant={score === n ? "default" : "secondary"}
              className="h-10 px-0 tabular-nums"
              onClick={() => setScore(n)}
            >
              {n}
            </Button>
          ))}
        </div>
        <div className="flex justify-between text-xs text-muted-foreground">
          <span>{nps ? "Not likely" : "Not satisfied"}</span>
          <span>{nps ? "Very likely" : "Very satisfied"}</span>
        </div>
      </div>
      <div className="flex flex-col gap-1.5">
        <Label htmlFor={commentId}>Anything to add? (optional)</Label>
        <Textarea
          id={commentId}
          rows={3}
          maxLength={2000}
          value={comment}
          onChange={(e) => setComment(e.target.value)}
        />
      </div>
      {send.isError ? (
        <p role="alert" className="text-sm text-danger-text">
          {send.error instanceof ApiError && send.error.status === 409
            ? "This survey was already answered."
            : "Your answer was not sent. Try again."}
        </p>
      ) : null}
      <Button type="submit" className="self-start" disabled={score === null || send.isPending}>
        {send.isPending ? "Sending…" : "Send"}
      </Button>
    </form>
  );
}
